import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Shield, Lock, Save, RotateCcw, Loader2, Users, AlertCircle,
  CheckCircle2, Search,
} from 'lucide-react';
import AppFooter from '../components/AppFooter';
import BackButton from '../components/BackButton';
import { Input } from '../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL =
  'https://customer-assets.emergentagent.com/job_yolo-crop-counter/artifacts/dqftlupc_Recurso%2021VR%20logos.png';

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Administrador',
  operator: 'Operador',
  cashier: 'Cajero',
  auditor: 'Auditor',
};

const ROLE_DESC = {
  super_admin: 'Acceso total al sistema. No editable.',
  admin: 'Gestion administrativa y operativa.',
  operator: 'Operacion en piso, registro de movimientos.',
  cashier: 'Caja, cobros y cierres de turno.',
  auditor: 'Solo lectura para control y auditoria.',
};

const ROLE_DOT = {
  super_admin: 'bg-purple-500',
  admin: 'bg-blue-500',
  operator: 'bg-emerald-500',
  cashier: 'bg-amber-500',
  auditor: 'bg-slate-400',
};

export default function PermissionsPage() {
  const { user, can, fetchPermissions } = useAuth();
  const [data, setData] = useState(null);
  const [working, setWorking] = useState(null); // role currently saving
  const [resetting, setResetting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState({}); // {role: {action: bool}}
  const [dirtyRoles, setDirtyRoles] = useState(new Set());

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/permissions/matrix`, { withCredentials: true });
        if (!alive) return;
        setData(data);
        setDraft(data?.matrix || {});
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'No se pudo cargar la matriz');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const grouped = useMemo(() => {
    if (!data?.actions) return [];
    const map = new Map();
    for (const a of data.actions) {
      if (!map.has(a.module)) map.set(a.module, { module: a.module, label: a.module_label, items: [] });
      map.get(a.module).items.push(a);
    }
    if (!search.trim()) return Array.from(map.values());
    const q = search.trim().toLowerCase();
    return Array.from(map.values())
      .map((g) => ({
        ...g,
        items: g.items.filter(
          (a) => a.label.toLowerCase().includes(q) || a.action.toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.items.length > 0);
  }, [data, search]);

  const editableRoles = data?.editable_roles || [];
  const roles = data?.roles || [];

  const toggleAction = (role, action, value) => {
    setDraft((prev) => ({
      ...prev,
      [role]: { ...(prev[role] || {}), [action]: value },
    }));
    setDirtyRoles((prev) => {
      const next = new Set(prev);
      next.add(role);
      return next;
    });
  };

  const setAllForRole = (role, value, actionsScope) => {
    if (!editableRoles.includes(role)) return;
    setDraft((prev) => {
      const cur = { ...(prev[role] || {}) };
      const list = actionsScope || (data?.actions || []).map((a) => a.action);
      list.forEach((a) => { cur[a] = value; });
      return { ...prev, [role]: cur };
    });
    setDirtyRoles((prev) => new Set(prev).add(role));
  };

  const saveRole = async (role) => {
    if (!editableRoles.includes(role)) return;
    setWorking(role);
    try {
      const payload = { role, permissions: draft[role] || {} };
      const { data: res } = await axios.put(`${API}/permissions/matrix`, payload, { withCredentials: true });
      setData((prev) => ({ ...prev, matrix: res.matrix }));
      setDraft(res.matrix);
      setDirtyRoles((prev) => {
        const next = new Set(prev);
        next.delete(role);
        return next;
      });
      toast.success(`Permisos de ${ROLE_LABELS[role]} guardados`);
      // refresh effective perms for current user (in case they edited their own role indirectly)
      fetchPermissions?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally {
      setWorking(null);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm('Restaurar permisos por defecto para los roles editables?')) return;
    setResetting(true);
    try {
      const { data: res } = await axios.post(`${API}/permissions/reset-defaults`, {}, { withCredentials: true });
      setData((prev) => ({ ...prev, matrix: res.matrix }));
      setDraft(res.matrix);
      setDirtyRoles(new Set());
      toast.success('Defaults restaurados');
      fetchPermissions?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al restaurar');
    } finally {
      setResetting(false);
    }
  };

  if (user && user.role !== 'super_admin' && user.role !== 'admin') {
    return <Navigate to="/dashboard" />;
  }
  if (user && !can('permissions.manage') && user.role !== 'super_admin') {
    return <Navigate to="/dashboard" />;
  }

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      <header className="bg-slate-800 border-b border-slate-700 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3 min-w-0">
              <BackButton to="/dashboard" />
              <div className="border-l border-slate-700 pl-3 min-w-0">
                <h1 className="text-base sm:text-lg font-bold text-white flex items-center gap-2 truncate">
                  <Lock className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  Matriz de permisos
                </h1>
                <p className="text-[11px] sm:text-xs text-slate-400 truncate">
                  Control granular por rol y modulo
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={resetDefaults}
                disabled={resetting}
                className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
                data-testid="reset-defaults-btn"
              >
                {resetting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
                <span className="hidden sm:inline">Defaults</span>
              </Button>
              <img src={LOGO_URL} alt="ParkingVision AI" className="h-8 w-auto" />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto w-full px-3 sm:px-4 lg:px-8 py-4 sm:py-6 flex-1">
        {loading ? (
          <div className="text-center py-12 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3" /> Cargando matriz...
          </div>
        ) : !data ? (
          <div className="text-center py-12 text-slate-400">No se pudo cargar la matriz</div>
        ) : (
          <>
            {/* Roles overview */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 mb-4">
              {roles.map((r) => {
                const editable = editableRoles.includes(r);
                const enabledCount = Object.values(draft[r] || {}).filter(Boolean).length;
                const total = (data.actions || []).length;
                return (
                  <Card key={r} className="bg-slate-800 border-slate-700 transition-colors hover:border-emerald-500/40">
                    <CardContent className="p-3">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className={`w-2 h-2 rounded-full ${ROLE_DOT[r]}`} />
                        <span className="text-sm font-semibold text-white truncate">{ROLE_LABELS[r] || r}</span>
                      </div>
                      <p className="text-[11px] text-slate-400 leading-tight line-clamp-2 mb-2">{ROLE_DESC[r]}</p>
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-slate-300">
                          {r === 'super_admin' ? 'Total' : `${enabledCount}/${total}`}
                        </span>
                        {editable ? (
                          <Badge className="bg-emerald-600/20 text-emerald-300 border border-emerald-500/40 text-[10px] px-1.5 py-0">Editable</Badge>
                        ) : (
                          <Badge className="bg-slate-700 text-slate-400 border border-slate-600 text-[10px] px-1.5 py-0">Bloqueado</Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Toolbar */}
            <Card className="bg-slate-800 border-slate-700 mb-4">
              <CardContent className="p-3 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar permiso..."
                    className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 pl-9"
                    data-testid="permissions-search"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
                  <Shield className="w-3.5 h-3.5 text-emerald-400" />
                  <span>
                    {user?.role === 'super_admin'
                      ? 'Editas todos los roles excepto super_admin (siempre acceso total).'
                      : 'Como admin podes editar Operador, Cajero y Auditor dentro de tu organizacion.'}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Matrix */}
            <Card className="bg-slate-800 border-slate-700 overflow-hidden">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-sm sm:text-base flex items-center gap-2">
                  <Users className="w-4 h-4 text-emerald-400" />
                  Permisos por modulo
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {/* Desktop: full matrix */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-900/40 sticky top-0">
                      <tr>
                        <th className="text-left text-xs font-semibold text-slate-300 uppercase tracking-wide px-4 py-3 w-[42%] min-w-[280px]">Permiso</th>
                        {roles.map((r) => (
                          <th key={r} className="text-center text-xs font-semibold text-slate-300 uppercase tracking-wide px-2 py-3 align-bottom">
                            <div className="flex flex-col items-center gap-1">
                              <span className="flex items-center gap-1">
                                <span className={`w-1.5 h-1.5 rounded-full ${ROLE_DOT[r]}`} />
                                {ROLE_LABELS[r] || r}
                              </span>
                              {editableRoles.includes(r) ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const allOn = (data?.actions || []).every((a) => Boolean(draft[r]?.[a.action]));
                                    setAllForRole(r, !allOn);
                                  }}
                                  className="text-[10px] text-emerald-300 hover:text-emerald-200 underline-offset-2 hover:underline normal-case font-medium"
                                  data-testid={`toggle-all-${r}`}
                                >
                                  todo / nada
                                </button>
                              ) : (
                                <span className="text-[10px] text-slate-500 normal-case">solo lectura</span>
                              )}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {grouped.map((group) => (
                        <PermGroupRows
                          key={group.module}
                          group={group}
                          roles={roles}
                          editableRoles={editableRoles}
                          draft={draft}
                          onToggle={toggleAction}
                          onSetGroup={(role, value) => setAllForRole(role, value, group.items.map((i) => i.action))}
                        />
                      ))}
                      {grouped.length === 0 && (
                        <tr>
                          <td colSpan={roles.length + 1} className="text-center text-slate-400 py-8">
                            Sin resultados para "{search}"
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Mobile: stacked per module */}
                <div className="md:hidden divide-y divide-slate-700">
                  {grouped.map((group) => (
                    <div key={group.module} className="p-3">
                      <div className="text-xs font-bold uppercase tracking-wide text-emerald-300 mb-2">{group.label}</div>
                      <div className="space-y-3">
                        {group.items.map((a) => (
                          <div key={a.action} className="bg-slate-900/40 border border-slate-700 rounded-lg p-3">
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-white truncate">{a.label}</div>
                                <div className="text-[10px] text-slate-500 font-mono truncate">{a.action}</div>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              {roles.map((r) => {
                                const editable = editableRoles.includes(r);
                                const checked = r === 'super_admin' ? true : Boolean(draft[r]?.[a.action]);
                                return (
                                  <label
                                    key={r}
                                    className={`flex items-center justify-between gap-2 px-2 py-1.5 rounded border ${
                                      editable
                                        ? 'border-slate-700 bg-slate-800/60'
                                        : 'border-slate-700/60 bg-slate-900/40 opacity-70'
                                    }`}
                                  >
                                    <span className="flex items-center gap-1.5 text-[11px] text-slate-300">
                                      <span className={`w-1.5 h-1.5 rounded-full ${ROLE_DOT[r]}`} />
                                      {ROLE_LABELS[r]}
                                    </span>
                                    <Switch
                                      checked={checked}
                                      onCheckedChange={(v) => editable && toggleAction(r, a.action, v)}
                                      disabled={!editable}
                                    />
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Save bar */}
            <div className="sticky bottom-0 mt-4 bg-slate-900/90 backdrop-blur border border-slate-700 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-2 text-xs text-slate-300">
                {dirtyRoles.size > 0 ? (
                  <>
                    <AlertCircle className="w-4 h-4 text-amber-400" />
                    Cambios sin guardar en: {Array.from(dirtyRoles).map((r) => ROLE_LABELS[r]).join(', ')}
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    Sin cambios pendientes
                  </>
                )}
              </div>
              <div className="flex flex-wrap gap-2 justify-end">
                {editableRoles.map((r) => (
                  <Button
                    key={r}
                    onClick={() => saveRole(r)}
                    disabled={!dirtyRoles.has(r) || working === r}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
                    size="sm"
                    data-testid={`save-role-${r}`}
                  >
                    {working === r ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
                    {ROLE_LABELS[r]}
                  </Button>
                ))}
              </div>
            </div>
          </>
        )}
      </main>

      <AppFooter />
    </div>
  );
}

function PermGroupRows({ group, roles, editableRoles, draft, onToggle, onSetGroup }) {
  return (
    <>
      <tr className="bg-slate-900/30">
        <td colSpan={roles.length + 1} className="px-4 py-2">
          <div className="flex items-center justify-between gap-3">
            <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-300">{group.label}</span>
            <div className="hidden lg:flex gap-1">
              {editableRoles.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => {
                    const allOn = group.items.every((i) => Boolean(draft[r]?.[i.action]));
                    onSetGroup(r, !allOn);
                  }}
                  className="text-[10px] text-slate-400 hover:text-emerald-300 px-1.5 py-0.5 rounded border border-slate-700 hover:border-emerald-500/40"
                >
                  {ROLE_LABELS[r]}
                </button>
              ))}
            </div>
          </div>
        </td>
      </tr>
      {group.items.map((a, idx) => (
        <tr
          key={a.action}
          className={`${idx % 2 === 0 ? 'bg-transparent' : 'bg-slate-900/20'} border-b border-slate-700/40 hover:bg-slate-700/20 transition-colors`}
        >
          <td className="px-4 py-2.5">
            <div className="text-sm text-white">{a.label}</div>
            <div className="text-[10px] text-slate-500 font-mono">{a.action}</div>
          </td>
          {roles.map((r) => {
            const editable = editableRoles.includes(r);
            const checked = r === 'super_admin' ? true : Boolean(draft[r]?.[a.action]);
            return (
              <td key={r} className="text-center px-2 py-2.5">
                <Switch
                  checked={checked}
                  onCheckedChange={(v) => editable && onToggle(r, a.action, v)}
                  disabled={!editable}
                  aria-label={`${a.label} para ${r}`}
                />
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}
